import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { UnitMap } from '@/shared/map/UnitMap'
import { SubjectPicker } from './EventDialog'
import { CLASSIFICATION_ORDER, EVENT_TYPE_ORDER, classificationLabel, eventTypeLabel } from './vocab'
import { explain } from '@/shared/api/problem'
import type { CoverageEventResponse, CoverageSubjectResponse } from '@/shared/api/types'
import { DateTimePicker } from '@/components/erp/date-picker'

/** `datetime-local` refuses a zoned ISO string, so the value is local-shifted. */
function forInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}

/**
 * Edit an event.
 *
 * The PATCH body is a **partial update where absent means "leave alone"**, so only
 * fields the user actually touched are sent. That is not an optimisation: the
 * server distinguishes absent from explicit null, and sending every field on
 * every save would let one person's stale form silently overwrite another's edit
 * to a field they never looked at.
 */
export function EditEventDialog({
  event, onClose, onSaved,
}: {
  event: CoverageEventResponse
  onClose: () => void
  onSaved: (patch: Record<string, unknown>) => Promise<void>
}) {
  const [title, setTitle] = useState(event.title)
  const [eventTypeCode, setEventTypeCode] = useState(event.eventTypeCode)
  const [classificationCode, setClassificationCode] = useState(event.classificationCode)
  const [venue, setVenue] = useState(event.venue ?? '')
  const [scheduledStart, setScheduledStart] = useState(forInput(event.scheduledStart))
  const [expected, setExpected] = useState(event.expectedAttendance?.toString() ?? '')
  const [lat, setLat] = useState<number | null>(
    event.latitude == null ? null : Number(event.latitude),
  )
  const [lon, setLon] = useState<number | null>(
    event.longitude == null ? null : Number(event.longitude),
  )
  const [description, setDescription] = useState(event.description ?? '')
  const [subjectType, setSubjectType] = useState(event.subject?.subjectTypeCode ?? '')
  const [subject, setSubject] = useState<CoverageSubjectResponse | null>(
    event.subject?.resolved ? event.subject : null,
  )
  const [subjectTouched, setSubjectTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const lowering = CLASSIFICATION_ORDER.indexOf(classificationCode as never)
    < CLASSIFICATION_ORDER.indexOf(event.classificationCode as never)

  return (
    <Modal open onClose={onClose} title="Edit event" width="lg">
      <div className="space-y-3">
        <ErrorStrip message={error} />

        <Field label="Title" required>
          <input className="erp-input w-full" value={title} autoFocus
            onChange={(e) => setTitle(e.target.value)} />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Type">
            <select className="erp-input w-full" value={eventTypeCode}
              onChange={(e) => setEventTypeCode(e.target.value)}>
              {EVENT_TYPE_ORDER.map((t) => <option key={t} value={t}>{eventTypeLabel(t)}</option>)}
            </select>
          </Field>
          <Field label="Who may see it">
            <select className="erp-input w-full" value={classificationCode}
              onChange={(e) => setClassificationCode(e.target.value)}>
              {CLASSIFICATION_ORDER.map((c) => (
                <option key={c} value={c}>{classificationLabel(c)}</option>
              ))}
            </select>
          </Field>
        </div>

        {lowering && (
          <Note tone="bad">
            You are widening who can see this, from {classificationLabel(event.classificationCode)} to{' '}
            {classificationLabel(classificationCode)}. Declassifying needs the coverage
            administration permission and is recorded as a security event.
          </Note>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Starts">
            <DateTimePicker value={scheduledStart} onChange={setScheduledStart} className="w-full" />
          </Field>
          <Field label="Venue">
            <input className="erp-input w-full" value={venue}
              onChange={(e) => setVenue(e.target.value)} />
          </Field>
        </div>

        <Field label="Expected attendance">
          <input type="number" min={0} className="erp-input w-full" value={expected}
            onChange={(e) => setExpected(e.target.value)} placeholder="Optional" />
        </Field>

        {/*
          Coordinates, because the Coverage Map is otherwise a screen that tells
          you what is missing and gives you no way to fix it. Click to place —
          typing a decimal degree for "the primary school past the market" is not
          a thing anybody does correctly. The numeric inputs stay for the case
          where somebody has a GPS reading, and for when there is no Mapbox token.
        */}
        <Field label="Place on the map" hint="Optional. Click the map, or type the coordinates.">
          <div className="space-y-2">
            <UnitMap
              mode="point"
              lat={lat}
              lon={lon}
              editable
              onPick={(la, lo) => { setLat(la); setLon(lo) }}
              label={event.title}
              className="h-56"
            />
            <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
              <input
                className="erp-input" type="number" step="any" placeholder="Latitude"
                aria-label="Latitude"
                value={lat ?? ''}
                onChange={(e) => setLat(e.target.value === '' ? null : Number(e.target.value))}
              />
              <input
                className="erp-input" type="number" step="any" placeholder="Longitude"
                aria-label="Longitude"
                value={lon ?? ''}
                onChange={(e) => setLon(e.target.value === '' ? null : Number(e.target.value))}
              />
              {(lat != null || lon != null) && (
                <button type="button" className="erp-btn"
                  onClick={() => { setLat(null); setLon(null) }}>
                  Clear
                </button>
              )}
            </div>
            {(lat == null) !== (lon == null) && (
              <Note tone="bad">
                Give both, or neither. Half a coordinate places nothing.
              </Note>
            )}
          </div>
        </Field>

        <SubjectPicker
          typeCode={subjectType}
          onTypeChange={(c) => { setSubjectType(c); setSubject(null); setSubjectTouched(true) }}
          selected={subject}
          onSelect={(sel) => { setSubject(sel); setSubjectTouched(true) }}
        />

        <Field label="Notes">
          <textarea className="erp-input min-h-[64px] w-full" value={description}
            onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={saving || !title.trim() || ((lat == null) !== (lon == null))}
            onClick={async () => {
              setSaving(true); setError(null)
              const patch: Record<string, unknown> = {}
              // Only what changed. Absent means "leave alone" server-side.
              if (title.trim() !== event.title) patch.title = title.trim()
              if (eventTypeCode !== event.eventTypeCode) patch.eventTypeCode = eventTypeCode
              if (classificationCode !== event.classificationCode) {
                patch.classificationCode = classificationCode
              }
              if ((venue.trim() || null) !== event.venue) patch.venue = venue.trim() || null
              if ((description.trim() || null) !== event.description) {
                patch.description = description.trim() || null
              }
              const startIso = scheduledStart ? new Date(scheduledStart).toISOString() : null
              if (startIso && startIso !== event.scheduledStart) patch.scheduledStart = startIso
              const att = expected ? Number(expected) : null
              if (att !== event.expectedAttendance) patch.expectedAttendance = att
              // Both or neither — the server enforces it and so does the button.
              const wasLat = event.latitude == null ? null : Number(event.latitude)
              const wasLon = event.longitude == null ? null : Number(event.longitude)
              if (lat !== wasLat || lon !== wasLon) {
                patch.latitude = lat
                patch.longitude = lon
              }
              // The subject travels as ONE object or an explicit null, never as
              // three independent fields — two of the eight combinations of those
              // are illegal and a partial update would produce them.
              if (subjectTouched) {
                patch.subject = subject
                  ? { subjectTypeCode: subject.subjectTypeCode, subjectRef: subject.ref }
                  : null
              }
              try { await onSaved(patch) } catch (e) { setError(explain(e)) } finally { setSaving(false) }
            }}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
          </button>
        </div>
      </div>
    </Modal>
  )
}
